/**
 * Notifications slice — in-app inbox state.
 *
 * `unreadCount` is derived on every mutation (cheap; the inbox is bounded
 * by user attention). `groupByTime` is a pure helper that the
 * NotifCenterScreen calls to render the "今日 / 更早" sections without
 * having to know about timezones.
 *
 * `resolveConflict` is also exported here as a pure helper because the
 * conflict-resolution flow is tightly coupled to the notification (the
 * conflict-kind notification's payload carries server_rev/local_rev).
 * It does NOT mutate any state — the caller wires the returned action
 * into the sync queue.
 */

import type { NotificationKind, NotificationRow } from '@/lib/sheets/types';

/**
 * Settable notification kinds — `system` notifications never have a toggle
 * (they're always-on emergency channel for upgrade prompts etc.).
 */
export const SETTABLE_KINDS = [
  'occ_conflict',
  'recurring_generated',
  'sync_failed',
] as const satisfies readonly NotificationKind[];

export type SettableKind = (typeof SETTABLE_KINDS)[number];

export type NotifSettings = Record<SettableKind, boolean>;

function isSettableKind(k: unknown): k is SettableKind {
  return typeof k === 'string' && (SETTABLE_KINDS as readonly string[]).includes(k);
}

export const DEFAULT_NOTIF_SETTINGS: NotifSettings = {
  occ_conflict: true,
  recurring_generated: true,
  sync_failed: true,
};

export interface NotificationsState {
  items: NotificationRow[];
  unreadCount: number;
  settings: NotifSettings;
}

export interface NotificationsActions {
  setItems: (items: NotificationRow[]) => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
  addNotification: (n: NotificationRow) => void;
  /**
   * Toggle a per-kind notification setting. Unknown kinds are silently
   * ignored (kind allowlist enforced — sharp edge per audit). Truthy
   * non-boolean values are coerced via `Boolean(value)`.
   */
  setSetting: (kind: SettableKind, enabled: boolean) => void;
  /** Replace the entire settings record (e.g. when hydrating from meta). */
  setSettings: (settings: Partial<NotifSettings>) => void;
}

export type NotificationsSlice = NotificationsState & NotificationsActions;

export const initialNotificationsState: NotificationsState = {
  items: [],
  unreadCount: 0,
  settings: { ...DEFAULT_NOTIF_SETTINGS },
};

function computeUnread(items: NotificationRow[]): number {
  let n = 0;
  for (const it of items) if (!it.read_at) n++;
  return n;
}

export const createNotificationsSlice =
  <T extends NotificationsSlice>() =>
  (
    set: (
      partial: Partial<T> | ((state: T) => Partial<T>),
      replace?: boolean,
    ) => void,
  ): NotificationsSlice => ({
    ...initialNotificationsState,

    setItems: (items) =>
      set({ items, unreadCount: computeUnread(items) } as Partial<T>),

    markRead: (id) =>
      set((state) => {
        const items = state.items.map((n) =>
          n.id === id && !n.read_at
            ? { ...n, read_at: new Date().toISOString() }
            : n,
        );
        return { items, unreadCount: computeUnread(items) } as Partial<T>;
      }),

    markAllRead: () =>
      set((state) => {
        const now = new Date().toISOString();
        const items = state.items.map((n) =>
          n.read_at ? n : { ...n, read_at: now },
        );
        return { items, unreadCount: 0 } as Partial<T>;
      }),

    addNotification: (n) =>
      set((state) => {
        // Drop notifications whose kind has been toggled off — `system`
        // never has a toggle so it always passes.
        if (isSettableKind(n.kind) && state.settings[n.kind] === false) {
          return {} as Partial<T>;
        }
        const items = [n, ...state.items];
        return { items, unreadCount: computeUnread(items) } as Partial<T>;
      }),

    setSetting: (kind, enabled) =>
      set((state) => {
        if (!isSettableKind(kind)) return {} as Partial<T>;
        const settings: NotifSettings = {
          ...state.settings,
          [kind]: Boolean(enabled),
        };
        return { settings } as Partial<T>;
      }),

    setSettings: (patch) =>
      set((state) => {
        const next: NotifSettings = { ...state.settings };
        for (const k of SETTABLE_KINDS) {
          if (k in patch && patch[k] !== undefined) {
            next[k] = Boolean(patch[k]);
          }
        }
        return { settings: next } as Partial<T>;
      }),
  });

/**
 * Group notifications into "today" (created on the same UTC calendar day
 * as `now`) and "earlier".
 */
export function groupByTime(
  items: NotificationRow[],
  now: Date,
): { today: NotificationRow[]; earlier: NotificationRow[] } {
  const todayKey = ymdUtc(now);
  const today: NotificationRow[] = [];
  const earlier: NotificationRow[] = [];
  for (const n of items) {
    if (!n.created_at) {
      earlier.push(n);
      continue;
    }
    const d = new Date(n.created_at);
    if (Number.isNaN(d.getTime())) {
      earlier.push(n);
      continue;
    }
    if (ymdUtc(d) === todayKey) today.push(n);
    else earlier.push(n);
  }
  return { today, earlier };
}

function ymdUtc(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

// ─── meta.notif_settings JSON serialization ─────────────────────────────────

export const NOTIF_SETTINGS_META_KEY = 'notif_settings';

/**
 * Serialize notif settings to the JSON shape stored in `meta.notif_settings`.
 * Intentionally shrinks to a stable Record so additions to NotifSettings
 * (e.g. future notification kinds) don't silently break older readers.
 */
export function serializeNotifSettings(settings: NotifSettings): string {
  const out: Record<SettableKind, boolean> = {
    occ_conflict: !!settings.occ_conflict,
    recurring_generated: !!settings.recurring_generated,
    sync_failed: !!settings.sync_failed,
  };
  return JSON.stringify(out);
}

/**
 * Parse the `meta.notif_settings` JSON string. Missing or malformed fields
 * fall back to `DEFAULT_NOTIF_SETTINGS`. Unknown keys in the JSON are
 * dropped — kind allowlist is enforced even at the deserialization layer
 * (sharp edge per audit).
 */
export function parseNotifSettings(raw: string | undefined | null): NotifSettings {
  if (!raw) return { ...DEFAULT_NOTIF_SETTINGS };
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ...DEFAULT_NOTIF_SETTINGS };
  }
  if (parsed == null || typeof parsed !== 'object') {
    return { ...DEFAULT_NOTIF_SETTINGS };
  }
  const obj = parsed as Record<string, unknown>;
  const out: NotifSettings = { ...DEFAULT_NOTIF_SETTINGS };
  for (const k of SETTABLE_KINDS) {
    if (k in obj && typeof obj[k] === 'boolean') {
      out[k] = obj[k] as boolean;
    }
  }
  return out;
}

// ─── OCC conflict resolution helper ──────────────────────────────────────────

export type ConflictChoice = 'local' | 'remote' | 'both';

interface PayloadShape {
  id: string;
  rev: number;
  [key: string]: unknown;
}

export type ConflictResolution =
  | { action: 'update'; payload: PayloadShape }
  | { action: 'discard_local'; payload: PayloadShape }
  | {
      action: 'keep_both';
      payload: { local: PayloadShape; remote: PayloadShape };
    };

function genId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `conflict-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Decide what to do with a conflict-kind notification given the user's
 * choice. Pure: returns the resolution payload but does not mutate state
 * or call the sync queue.
 *
 * - `local`: keep local edit, advance rev to (local.rev + 1) — caller
 *   enqueues an update_tx.
 * - `remote`: discard local, return remote payload as-is — caller
 *   refreshes the local store from it.
 * - `both`: emit a brand-new row using local payload (new id, rev=1) and
 *   leave the remote row untouched. Caller enqueues a create_tx for the
 *   local copy.
 */
export function resolveConflict(
  local: PayloadShape,
  remote: PayloadShape,
  choice: ConflictChoice,
): ConflictResolution {
  if (choice === 'local') {
    return {
      action: 'update',
      payload: { ...local, rev: local.rev + 1 },
    };
  }
  if (choice === 'remote') {
    return { action: 'discard_local', payload: remote };
  }
  // both
  return {
    action: 'keep_both',
    payload: {
      local: { ...local, id: genId(), rev: 1 },
      remote,
    },
  };
}
